import React, { useState, useMemo, useCallback } from 'react';
import {
    Drawer,
    TextInput,
    Switch,
    Button,
    Text,
    Stack,
    Box,
    NativeSelect,
    Flex,
    Group,
    ScrollArea,
    Loader,
    Divider,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useForm, yupResolver } from '@mantine/form';
import * as Yup from 'yup';
import { IconSearch, IconCirclePlus } from '@tabler/icons-react';
import clsx from 'clsx';
import {
    showSuccessNotification,
    showErrorNotification,
} from '../../notifications';
import {
    useGetCompetitors,
    useCreateCompetitor,
} from '../../services/competitor/competitor.service';
import type { Competitor, PriceSurvey } from '../../codegen/graphql';
import {
    getErrorMessage,
    getSurveyCompetitorScopeInput,
} from '../../pages/price-surveys/helpers';
import { callingCodes } from '../../constants';
import { numberObjectMapper } from '../../utility/numberObjectMapper';
import { CompetitorListEmpty } from './CompetitorListEmpty';
import { CompetitorListRow } from './CompetitorListRow';

const CALLING_CODE_OPTIONS = callingCodes.filter(o => o.value !== '');

interface AddCompetitorFormValues {
    name: string;
    address: string;
    telNo: {
        code: string;
        number: string;
    };
    email: string;
    isMajor: boolean;
}

const addCompetitorInitialValues: AddCompetitorFormValues = {
    name: '',
    address: '',
    telNo: {
        code: 'ZA/27',
        number: '',
    },
    email: '',
    isMajor: false,
};

const addCompetitorValidationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    address: Yup.string().required('Required'),
    telNo: Yup.object().shape({
        code: Yup.string().required('Required'),
        number: Yup.string().test(
            'tel-number',
            'Required',
            function telNumberTest(value) {
                const trimmed = (value ?? '').replace(/\s/g, '');
                if (!trimmed) {
                    return this.createError({
                        message: 'Required',
                    });
                }
                if (!/^\d+$/.test(trimmed)) {
                    return this.createError({
                        message: 'Phone number must contain only digits',
                    });
                }
                if (trimmed.length < 9) {
                    return this.createError({
                        message: 'Telephone number too short',
                    });
                }
                return true;
            },
        ),
    }),
    email: Yup.string().email('Not a valid email'),
    isMajor: Yup.boolean().required(),
});

interface Props {
    opened: boolean;
    onClose: () => void;
    survey: PriceSurvey;
    existingCompetitorIds: Set<string>;
    onCompetitorAdd: (competitor: Competitor) => void;
}

export function AddCompetitorDrawer({
    opened,
    onClose,
    survey,
    existingCompetitorIds,
    onCompetitorAdd,
}: Props) {
    const [view, setView] = useState<'search' | 'create'>('search');
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search.trim(), 250);

    const form = useForm<AddCompetitorFormValues>({
        initialValues: addCompetitorInitialValues,
        validate: yupResolver(addCompetitorValidationSchema),
        validateInputOnBlur: true,
    });

    const competitorFilter = useMemo(() => {
        return {
            ...getSurveyCompetitorScopeInput(survey),
            ...(debouncedSearch ? { name: debouncedSearch } : {}),
        };
    }, [survey, debouncedSearch]);

    const {
        data: competitorsData,
        loading: loadingCompetitors,
        refetch: refetchCompetitors,
    } = useGetCompetitors(competitorFilter, {
        fetchPolicy: 'cache-and-network',
        skip: !opened,
    });

    const competitors: Competitor[] = useMemo(
        () => competitorsData?.getCompetitors || [],
        [competitorsData?.getCompetitors],
    );

    const [createCompetitor, { loading: creating }] = useCreateCompetitor();

    const handleBackToSearch = () => {
        setView('search');
        form.reset();
    };

    const handleClose = useCallback(() => {
        setView('search');
        setSearch('');
        form.reset();
        onClose();
    }, [form, onClose]);

    const handlePickCompetitor = useCallback(
        (competitor: Competitor) => {
            if (existingCompetitorIds.has(competitor.id)) {
                return;
            }
            if (!competitor.name) {
                showErrorNotification(
                    'The selected competitor is no longer available. Please try again.',
                );
                return;
            }
            onCompetitorAdd(competitor);
            handleClose();
        },
        [existingCompetitorIds, onCompetitorAdd, handleClose],
    );

    const handleCreate = async (values: typeof form.values) => {
        try {
            const telNo = numberObjectMapper(
                values.telNo.number,
                values.telNo.code,
            );

            if (!telNo.dialingCode) {
                showErrorNotification('Invalid country code selection');
                return;
            }

            const { data } = await createCompetitor({
                variables: {
                    input: {
                        name: values.name.trim(),
                        address: values.address?.trim() || '',
                        email: values.email?.trim() || '',
                        telNo,
                        isMajor: values.isMajor,
                        ...getSurveyCompetitorScopeInput(survey),
                    },
                },
            });

            if (data?.createCompetitor?.id && data.createCompetitor) {
                onCompetitorAdd(data.createCompetitor);
                // eslint-disable-next-line no-void
                void refetchCompetitors().catch(error =>
                    showErrorNotification(getErrorMessage(error)),
                );
                showSuccessNotification(
                    'Competitor created and added to survey',
                );
                handleClose();
            }
        } catch (error: unknown) {
            showErrorNotification(getErrorMessage(error));
        }
    };

    const renderListContent = () => {
        if (loadingCompetitors && competitors.length === 0) {
            return (
                <Flex
                    justify="center"
                    align="center"
                    className="min-h-[200px] bg-white py-10"
                >
                    <Loader size="sm" />
                </Flex>
            );
        }

        if (!loadingCompetitors && competitors.length === 0) {
            return (
                <CompetitorListEmpty
                    isSearchResults={Boolean(debouncedSearch)}
                />
            );
        }

        return (
            <Stack gap={0} className="bg-white">
                {competitors.map((competitor, index) => {
                    const alreadyAdded = existingCompetitorIds.has(
                        competitor.id,
                    );
                    return (
                        <React.Fragment key={competitor.id}>
                            <CompetitorListRow
                                competitor={competitor}
                                disabled={alreadyAdded}
                                onPick={handlePickCompetitor}
                            />
                            {index < competitors.length - 1 ? (
                                <Divider color="#E5E5E5" />
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </Stack>
        );
    };

    return (
        <Drawer
            opened={opened}
            onClose={handleClose}
            title="Add Competitor"
            position="right"
            size="md"
            padding={0}
            withCloseButton
            classNames={{
                content: 'bg-[#f4f5f7] flex flex-col max-h-full',
                header: 'bg-[#f4f5f7] px-6 pt-6 pb-4',
                title: 'text-primary font-semibold text-lg',
                body: 'p-0 flex flex-col flex-1 min-h-0 overflow-hidden bg-[#f4f5f7]',
                close: 'mt-1',
            }}
        >
            <Box
                className={clsx(
                    'flex flex-col flex-1 min-h-0 p-6',
                    view === 'search' ? 'overflow-hidden' : 'overflow-y-auto',
                )}
            >
                {view === 'search' ? (
                    <Stack gap="md" className="flex flex-col flex-1 min-h-0">
                        <Flex
                            align="center"
                            wrap="nowrap"
                            gap="sm"
                            w="100%"
                            className="shrink-0"
                        >
                            <Box className="min-w-0 flex-1" />
                            <Button
                                onClick={() => setView('create')}
                                radius="xl"
                                className="w-fit shrink-0 bg-primary text-white hover:bg-primary/90 border-0 text-sm font-medium h-auto py-2.5 px-4"
                            >
                                <Group gap={10} align="center" wrap="nowrap">
                                    <span className="leading-none">
                                        Add New
                                    </span>
                                    <IconCirclePlus size={16} />
                                </Group>
                            </Button>
                        </Flex>

                        <TextInput
                            placeholder="Search Competitors"
                            value={search}
                            onChange={e => setSearch(e.currentTarget.value)}
                            leftSection={<IconSearch size={16} />}
                            rightSection={
                                loadingCompetitors ? (
                                    <Loader size="xs" />
                                ) : undefined
                            }
                            size="md"
                            radius="lg"
                            className="w-full shrink-0"
                        />

                        {competitors.some(c =>
                            existingCompetitorIds.has(c.id),
                        ) &&
                        competitors.some(
                            c => !existingCompetitorIds.has(c.id),
                        ) ? (
                            <Text size="xs" c="dimmed" className="shrink-0">
                                Competitors already on this survey appear dimmed
                            </Text>
                        ) : null}

                        <ScrollArea
                            classNames={{
                                root: 'flex-1 min-h-0 rounded-lg border border-[#E5E5E5] bg-white overflow-hidden',
                                viewport: 'rounded-lg',
                            }}
                            type="auto"
                            offsetScrollbars
                        >
                            {renderListContent()}
                        </ScrollArea>
                    </Stack>
                ) : (
                    <Box>
                        <form onSubmit={form.onSubmit(handleCreate)}>
                            <Stack gap="md">
                                <Text fw={500} size="sm" c="dark.7">
                                    Fill in details to add a new competitor
                                </Text>

                                <TextInput
                                    required
                                    label="Competitor Name"
                                    placeholder="Enter competitor name"
                                    radius="md"
                                    size="sm"
                                    {...form.getInputProps('name')}
                                />

                                <TextInput
                                    required
                                    label="Street Address"
                                    placeholder="Enter street address"
                                    radius="md"
                                    size="sm"
                                    {...form.getInputProps('address')}
                                />

                                <div>
                                    <Text size="sm" fw={500} className="mb-1">
                                        Telephone Number{' '}
                                        <span className="text-red-500">*</span>
                                    </Text>
                                    <Flex
                                        gap="sm"
                                        align="flex-start"
                                        wrap="nowrap"
                                    >
                                        <NativeSelect
                                            data={CALLING_CODE_OPTIONS}
                                            size="sm"
                                            {...form.getInputProps(
                                                'telNo.code',
                                            )}
                                            className="min-w-[120px] w-[min(100%,160px)] shrink-0"
                                        />
                                        <TextInput
                                            required
                                            placeholder="e.g. 123456789"
                                            type="tel"
                                            radius="md"
                                            size="sm"
                                            className="min-w-0 flex-1"
                                            {...form.getInputProps(
                                                'telNo.number',
                                            )}
                                        />
                                    </Flex>
                                </div>

                                <TextInput
                                    label="Email Address"
                                    placeholder="e.g. contact@example.com"
                                    radius="md"
                                    size="sm"
                                    {...form.getInputProps('email')}
                                />

                                <Switch
                                    label="Major Competitor"
                                    size="md"
                                    thumbIcon={
                                        form.values.isMajor ? (
                                            <IconCirclePlus
                                                size="0.8rem"
                                                color="white"
                                                stroke={3}
                                            />
                                        ) : null
                                    }
                                    color={
                                        form.values.isMajor
                                            ? 'green'
                                            : undefined
                                    }
                                    {...form.getInputProps('isMajor', {
                                        type: 'checkbox',
                                    })}
                                />

                                <Box className="w-full overflow-hidden">
                                    <Flex
                                        justify="flex-end"
                                        mt="md"
                                        gap="sm"
                                        wrap="nowrap"
                                    >
                                        <Button
                                            variant="outline"
                                            onClick={handleBackToSearch}
                                            radius="xl"
                                            size="sm"
                                            className="flex-[1_1_0] border-[#dee2e6] text-[#003B7E]"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            type="submit"
                                            loading={creating}
                                            radius="xl"
                                            size="sm"
                                            className="flex-[1_1_0] bg-[#003B7E] hover:bg-[#002F66]"
                                        >
                                            Add
                                        </Button>
                                    </Flex>
                                </Box>
                            </Stack>
                        </form>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}
